/**
 * Export Agent Modal
 *
 * Shows the target agent, an "Include API keys" toggle (off by default —
 * safe path), and a Download button that converts the backend's exported
 * envelope into a JSON Blob and triggers a browser download.
 *
 * Secrets-included exports get a loud red warning so a fumble can't
 * silently leak credentials.
 */
import { useState } from 'react';
import { Download, X, AlertTriangle } from 'lucide-react';
import type { Agent } from '@/types/agent.types';
import type { ExportEnvelope } from '@/services/agentExportImport/schema';
import { buildExportFilename } from '@/services/agentExportImport/filename';
import { Button } from '@/components/Common';

interface ExportAgentModalProps {
  isOpen: boolean;
  agent: Agent | null;
  onClose: () => void;
  onExport: (args: { includeSecrets: boolean }) => Promise<ExportEnvelope>;
}

export function ExportAgentModal({
  isOpen,
  agent,
  onClose,
  onExport,
}: ExportAgentModalProps) {
  const [includeSecrets, setIncludeSecrets] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen || !agent) return null;

  const handleDownload = async () => {
    setIsExporting(true);
    setError(null);
    try {
      const envelope = await onExport({ includeSecrets });
      const blob = new Blob([JSON.stringify(envelope, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = buildExportFilename(agent.name, new Date());
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to export agent. Try again.'
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        role="dialog"
        aria-labelledby="export-agent-modal-title"
        className="w-full max-w-md rounded-xl bg-bg-primary p-6 shadow-2xl"
      >
        <div className="flex items-center justify-between">
          <h2
            id="export-agent-modal-title"
            className="text-lg font-semibold text-text-primary"
          >
            Export agent
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-text-tertiary hover:bg-bg-secondary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stellar-primary"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 rounded-lg border border-border-default p-3">
          <div className="text-sm text-text-secondary">Agent</div>
          <div className="font-medium text-text-primary">{agent.name}</div>
          <div className="text-xs text-text-tertiary">{agent.framework}</div>
        </div>

        <label className="mt-5 flex items-start gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={includeSecrets}
            aria-label="Include API keys"
            onClick={() => setIncludeSecrets((v) => !v)}
            className={`relative mt-0.5 inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stellar-primary focus-visible:ring-offset-2 ${
              includeSecrets ? 'bg-stellar-primary' : 'bg-bg-tertiary'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-bg-primary transition-transform ${
                includeSecrets ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
          <span>
            <span className="block text-sm font-medium text-text-primary">
              Include API keys
            </span>
            <span className="block text-xs text-text-secondary">
              When off (default), the file is safe to share. Recipients fill in
              keys during import.
            </span>
          </span>
        </label>

        {includeSecrets && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-error bg-error-light p-3 text-sm text-error">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>
              This file will contain live API keys. Treat it like a password —
              do not commit to git or share over chat.
            </span>
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="mt-3 rounded-lg border border-error bg-error-light p-3 text-sm text-error"
          >
            {error}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isExporting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownload}
            disabled={isExporting}
            leftIcon={<Download className="h-4 w-4" />}
          >
            {isExporting ? 'Preparing…' : 'Download .json'}
          </Button>
        </div>
      </div>
    </div>
  );
}
